import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import U2000ActionBtn from '../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000CheckBoxBase from '../../../u2000Core/components/formControls/checkbox/U2000CheckBoxBase';
import { NS } from '../../U3146';
import {
  u3146PreviewLineIsEligible,
  type U3146PreviewLine,
} from '../../U3146Service';
import U3146CloseInactiveWizardPreviewGrid from './U3146CloseInactiveWizardPreviewGrid';

export type U3146CloseInactiveWizardConfirmStepProps = {
  lines: U3146PreviewLine[];
  selectedProjectIds: Set<string>;
  onToggleProject: (projectId: string, checked: boolean) => void;
  onToggleAllEligible: (checked: boolean, visibleEligibleProjectIds?: string[]) => void;
  selectedEligibleCount: number;
  excludedCount: number;
  writeOffCount: number;
  openInvoiceCount: number;
  canClose: boolean;
  onBack: () => void;
  onArchive: () => void;
  archiving: boolean;
};

const U3146CloseInactiveWizardConfirmStep: React.FC<U3146CloseInactiveWizardConfirmStepProps> = ({
  lines,
  selectedProjectIds,
  onToggleProject,
  onToggleAllEligible,
  selectedEligibleCount,
  excludedCount,
  writeOffCount,
  openInvoiceCount,
  canClose,
  onBack,
  onArchive,
  archiving,
}) => {
  const { t } = useTranslation();
  const [excludeIneligible, setExcludeIneligible] = useState(false);
  const hasEligibleLines = lines.some(u3146PreviewLineIsEligible);
  const closeDisabled = !canClose || selectedEligibleCount === 0 || archiving || !hasEligibleLines;
  const closeDisabledTitle = !canClose
    ? t('errWizardCloseDisabledNoRole', { ns: NS })
    : selectedEligibleCount === 0 || !hasEligibleLines
      ? t('errWizardCloseDisabledNoSelection', { ns: NS })
      : undefined;

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
      <button type="button" className="shrink-0 self-start text-sm text-blue-600 hover:underline" onClick={onBack}>
        ← {t('wizardBackToParams', { ns: NS })}
      </button>

      <div
        role="status"
        aria-live="polite"
        className="shrink-0 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950"
      >
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <p className="font-medium">
            {t('wizardConfirmSummary', { ns: NS, selected: selectedEligibleCount, excluded: excludedCount })}
          </p>
          <U2000CheckBoxBase
            fieldName="u3146WizardExcludeIneligible"
            label={t('wizardPreviewExcludeIneligible', { ns: NS })}
            value={excludeIneligible}
            setValue={setExcludeIneligible}
            readonly={archiving}
            className="shrink-0"
          />
        </div>
        {writeOffCount > 0 && (
          <p className="mt-2">{t('wizardConfirmWriteOffWarning', { ns: NS, count: writeOffCount })}</p>
        )}
        {openInvoiceCount > 0 && (
          <p className="mt-2">{t('wizardConfirmInvoiceCloseWarning', { ns: NS, count: openInvoiceCount })}</p>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <U3146CloseInactiveWizardPreviewGrid
          lines={lines}
          selectedProjectIds={selectedProjectIds}
          onToggleProject={onToggleProject}
          onToggleAllEligible={onToggleAllEligible}
          excludeIneligible={excludeIneligible}
          readonly={archiving}
        />
      </div>

      <div className="flex shrink-0 justify-end gap-2 pb-1">
        <U2000ActionBtn
          label={archiving ? t('wizardClosingProjects', { ns: NS }) : t('wizardArchiveProjects', { ns: NS })}
          handleClick={onArchive}
          disabled={closeDisabled}
          title={closeDisabled ? closeDisabledTitle : undefined}
        />
      </div>
    </section>
  );
};

export default U3146CloseInactiveWizardConfirmStep;
